import { useCallback, useMemo, useState } from 'react'

import { F0Box, F0Button, F0Text } from '@factorialco/f0-react'
import { Input, Switch } from '@factorialco/f0-react/dist/experimental'
import { Box, Card, CollapsableCard } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import AddFieldModal from './_AddFieldModal'
import type { NumerationState } from './index'
import { sectionLabel } from './translations'
import { SECTION_ORDER, type MergedField, type SectionType } from './types'

type OpenPanel = SectionType | 'numeration' | null

type Props = {
  fields: MergedField[]
  numeration: NumerationState
  onToggleVisibility: (fieldKey: string, newVisible: boolean) => void
  onAddCustomField: (
    section: SectionType,
    label: string,
    fieldType: 'text' | 'select',
    options: string[]
  ) => void
  onNumerationChange: (numeration: NumerationState) => void
}

function SideContent({
  fields,
  numeration,
  onToggleVisibility,
  onAddCustomField,
  onNumerationChange,
}: Props) {
  const [openPanel, setOpenPanel] = useState<OpenPanel>('numeration')
  const [addingSection, setAddingSection] = useState<SectionType | null>(null)

  const fieldsBySection = useMemo(() => {
    const grouped = new Map<SectionType, MergedField[]>()
    for (const section of SECTION_ORDER) {
      grouped.set(section, [])
    }
    for (const field of fields) {
      const sectionFields = grouped.get(field.sectionType)
      if (sectionFields) {
        sectionFields.push(field)
      }
    }
    for (const sectionFields of grouped.values()) {
      sectionFields.sort((a, b) => a.position - b.position)
    }
    return grouped
  }, [fields])

  const togglePanel = useCallback((panel: OpenPanel) => {
    setOpenPanel((prev) => (prev === panel ? null : panel))
  }, [])

  return (
    <F0Box display="flex" flexDirection="column" gap="md" padding="md">
      <CollapsableCard
        open={openPanel === 'numeration'}
        contentPadding="s0"
        contentBackground="grey000White"
        Card={
          <Box onClick={() => togglePanel('numeration')} paddingX="s8" gap="s8">
            <Card.Title size="small">
              {i18n.t('procurement.purchase_requests.settings.po_templates.detail.numeration')}
            </Card.Title>
          </Box>
        }
        Content={
          <F0Box display="flex" flexDirection="column" gap="md" padding="md">
            <Input
              label={i18n.t(
                'procurement.purchase_requests.settings.po_templates.detail.numeration_prefix'
              )}
              value={numeration.prefix}
              onChange={(value) => onNumerationChange({ ...numeration, prefix: value })}
              placeholder="PO"
              required
            />
            <Input
              label={i18n.t(
                'procurement.purchase_requests.settings.po_templates.detail.numeration_format'
              )}
              value={numeration.format}
              onChange={(value) => onNumerationChange({ ...numeration, format: value })}
              placeholder="0000"
              required
            />
          </F0Box>
        }
      />

      {SECTION_ORDER.map((section) => {
        const sectionFields = fieldsBySection.get(section) ?? []
        const isOpen = openPanel === section

        return (
          <CollapsableCard
            key={section}
            open={isOpen}
            contentPadding="s0"
            contentBackground="grey000White"
            Card={
              <Box onClick={() => togglePanel(section)} paddingX="s8" gap="s8">
                <Card.Title size="small">{sectionLabel(section)}</Card.Title>
              </Box>
            }
            Content={
              <F0Box display="flex" flexDirection="column">
                {sectionFields.map((field) => (
                  <F0Box
                    key={field.key}
                    display="flex"
                    alignItems="center"
                    justifyContent="between"
                    paddingX="md"
                    paddingY="sm"
                  >
                    <F0Text content={field.displayLabel} variant="body" />
                    <Switch
                      checked={field.visible}
                      onCheckedChange={(checked) => onToggleVisibility(field.key, checked)}
                      title={field.displayLabel}
                      hideLabel
                    />
                  </F0Box>
                ))}

                {section !== 'notes_and_delivery' && (
                  <F0Box padding="md">
                    <F0Button
                      label={i18n.t(
                        'procurement.purchase_requests.settings.po_templates.detail.add_field'
                      )}
                      variant="outline"
                      size="sm"
                      onClick={() => setAddingSection(section)}
                    />
                  </F0Box>
                )}
              </F0Box>
            }
          />
        )
      })}

      {addingSection && (
        <AddFieldModal
          section={addingSection}
          onClose={() => setAddingSection(null)}
          onAdd={onAddCustomField}
        />
      )}
    </F0Box>
  )
}

export default SideContent
